/**
 * Authentication configuration
 * Supports multiple authentication providers
 * Reads from window._env_ (FastAPI runtime) or window._env_ (Vite build-time)
 */

import { 
    getAzureClientId, 
    getAzureAuthority, 
    getRedirectUri, 
    getAzureScopes 
} from '@core/utils/env.utils';

export enum AuthProvider {
    LOCAL = 'local',
    AZURE_AD = 'azure-ad',
}

export interface AuthConfig {
    provider: AuthProvider;
    isValid: boolean;
    azureAd: {
        clientId: string;
        authority: string;
        redirectUri: string;
        scopes: string[];
    };
}

/**
 * Get authentication configuration from environment
 * Priority: window._env_ (FastAPI) > window._env_ (Vite)
 * ALWAYS uses real Azure AD - mock mode is completely removed
 * Returns config even if credentials are missing (isValid will be false)
 */
export const getAuthConfig = (): AuthConfig => {
    // Always use Azure AD provider - no local login form, no mock mode
    const provider = AuthProvider.AZURE_AD;
    
    // Always get Azure AD configuration - no mock fallback
    const clientId = getAzureClientId();
    const authority = getAzureAuthority();
    const redirectUri = getRedirectUri();
    const scopes = getAzureScopes();

    // Check if credentials are valid
    const isValid = !!(clientId && authority);

    // Note: We don't log errors here because:
    // 1. getAuthConfig() may be called before window._env_ is loaded (causing false errors)
    // 2. Azure AD works correctly, so config loads eventually
    // 3. User-facing errors are handled in Login.tsx and other components
    // 4. validateAuthConfig() throws errors when actually needed (before auth operations)

    const config: AuthConfig = {
        provider,
        isValid,
        azureAd: {
            clientId: clientId || '',
            authority: authority || '',
            redirectUri,
            scopes,
        },
    };

    // NOTE: Auth is disabled - no console logs
    // const mode = useMockAzureAD ? 'Mock Azure AD' : provider === AuthProvider.AZURE_AD ? 'Real Azure AD' : 'Local';
    // console.log(`✅ Auth provider initialized: ${mode}`);
    // if (config.azureAd) {
    //     console.log(`🔧 Azure AD Redirect URI: ${config.azureAd.redirectUri}`);
    // }

    return config;
};

/**
 * Validate that Azure AD configuration is complete
 * Throws error if config is invalid - use this before attempting authentication
 */
export const validateAuthConfig = (config: AuthConfig): void => {
    if (!config.isValid || !config.azureAd.clientId || !config.azureAd.authority) {
        throw new Error('Azure AD configuration is missing. Cannot proceed with authentication.');
    }
};

// Note: Do not export authConfig at module level - always call getAuthConfig() dynamically
// This ensures window._env_ is loaded before reading config, and errors are handled gracefully
